<script setup="">
import { ArchiveIcon, BarChartBigIcon, ContactRoundIcon, HomeIcon, SettingsIcon, StickyNoteIcon, StoreIcon } from "lucide-vue-next";

import Badge from "@/Components/Badge.vue";
import NumberFormat from "@/Components/NumberFormat.vue";

import useSidebar from "@/composables/sidebar";
import { Link } from "@inertiajs/vue3";

const props = defineProps({
  team: {
    type: Object,
    required: true
  }
});

const sidebar = useSidebar();

const navs = [
  {
    name: "홈",
    icon: HomeIcon,
    href: route("app.campaigns.index", props.team),
    current: route().current("app.campaigns.*"),
    enable: true
  },
  {
    name: "페이지",
    icon: StickyNoteIcon,
    href: route("app.pages.index", props.team),
    current: route().current("app.pages.*"),
    enable: true
  },
  {
    name: "폼",
    icon: ArchiveIcon,
    href: route("app.forms.index", props.team),
    current: route().current("app.forms.*"),
    enable: true
  },
  {
    name: "스토어",
    icon: StoreIcon,
    href: route("app.catalogs.products.index", props.team),
    current: route().current("app.catalogs.products.*"),
    new: "beta",
    enable: true
  },
  {
    name: "연락처",
    icon: ContactRoundIcon,
    href: route("app.contacts.index", props.team),
    current: route().current("app.contacts.*"),
    new: "beta",
    enable: true
  },
  {
    name: "통계",
    icon: BarChartBigIcon,
    href: route("app.statistics.team.show", props.team),
    current: route().current("app.statistics.team.*"),
    enable: true
  },
  {
    name: "설정",
    icon: SettingsIcon,
    href: route("teams.show", props.team),
    current: route().current("teams.show"),
    enable: true
  }
];
</script>

<template>
  <div class="menus flex" role="menu">
    <ul class="w-full space-y-1">
      <template v-for="(nav, index) in navs">
        <li v-if="nav.enable" :key="index" class="h-14" :class="[sidebar.isPrimaryMinimized.value ? 'flex-center' : '']">
          <Link
            :href="nav.href"
            class="group relative flex py-3 text-base tracking-wide outline-none ring-0 transition-all focus:outline-0 dark:border-zinc-700"
            :class="[sidebar.isPrimaryMinimized.value ? 'items-center justify-center px-4' : 'justify-between px-3']"
          >
            <div
              class="pointer-events-none absolute inset-0 z-0 select-none rounded-xl"
              :class="[nav.current ? 'bg-200/50 dark:group-hover:bg-200' : 'group-hover:bg-200/50 dark:group-hover:bg-zinc-800']"
            ></div>
            <!--end::hover-->

            <div class="relative flex items-center space-x-4">
              <span class="relative">
                <component
                  :is="nav.icon"
                  v-tippy="{ content: nav.name, placement: 'right' }"
                  class="minimized h-6 w-6 transition focus:outline-none focus:ring-0 group-hover:scale-110 group-hover:dark:text-white"
                  :class="[nav.current ? 'text-primary dark:text-zinc-100' : 'text-700 group-hover:text-900']"
                />
                <component
                  :is="nav.icon"
                  class="expanded h-6 w-6 transition group-hover:scale-110 group-hover:dark:text-white"
                  :class="[nav.current ? 'text-900' : 'text-700 group-hover:text-900']"
                />
                <span v-if="nav.count && nav.count.value > 0" class="flex-center absolute -right-1 top-0 z-1 h-4 w-4">
                  <span class="flex-center h-4 w-4 animate-pulse whitespace-nowrap rounded-full bg-red-500 text-tiny font-bold text-white">
                    <span class="inline-flex items-center">
                      <NumberFormat :value="nav.count.value" />
                    </span>
                  </span>
                </span>
              </span>

              <span class="expanded whitespace-nowrap text-900" :class="[nav.current ? 'font-bold' : '']" v-text="nav.name"></span>

              <div class="expanded">
                <Badge v-if="nav.new === true" variant="primary" class="tiny"> NEW</Badge>
                <Badge v-if="nav.new === 'beta'" variant="primary" outline class="tiny">BETA</Badge>
              </div>
            </div>
            <div class="expanded relative font-black" :class="nav.current ? 'text-primary dark:text-zinc-100' : 'text-200 group-hover:text-400'">
              •
            </div>
          </Link>
        </li>
      </template>
    </ul>
  </div>
</template>
